import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaClipboardList, FaSync, FaSearch, FaCheckCircle, FaTimesCircle, FaClock,
  FaExclamationTriangle, FaChevronDown, FaChevronRight, FaCopy, FaBan, FaUndo, FaMinusCircle
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { adminService } from '../services/adminService';

// ─── Order status model ──────────────────────────────────────────────────────
// provisionStatus on the customer record is the authoritative state; the Vocus TCAS
// state (QUEUED → PROCESSING → SUCCESS/FAILED/WITHDRAWN) rides along in
// vocusOrderSnapshot.lastStatus and is what "Check status" refreshes.
const STATUS = {
  pending:      { label: 'In progress', tone: 'amber',   icon: FaClock,               hint: 'Submitted to Vocus, awaiting activation' },
  active:       { label: 'Active',      tone: 'green',   icon: FaCheckCircle,         hint: 'Service is live' },
  failed:       { label: 'Failed',      tone: 'red',     icon: FaTimesCircle,         hint: 'Vocus rejected the order' },
  inactive:     { label: 'Failed',      tone: 'red',     icon: FaTimesCircle,         hint: 'Order did not complete' },
  withdrawn:    { label: 'Withdrawn',   tone: 'grey',    icon: FaUndo,                hint: 'Order withdrawn before activation' },
  cancelled:    { label: 'Cancelled',   tone: 'grey',    icon: FaBan,                 hint: 'Service deactivated at Vocus' },
  unordered:    { label: 'Not ordered', tone: 'blue',    icon: FaMinusCircle,         hint: 'Approved customer with no Vocus order yet' },
};
const TONES = {
  amber: 'bg-[#d79921]/20 text-[#fabd2f] border-[#d79921]/50',
  green: 'bg-[#98971a]/20 text-[#b8bb26] border-[#98971a]/50',
  red:   'bg-[#cc241d]/20 text-[#fb4934] border-[#cc241d]/50',
  grey:  'bg-[#504945]/40 text-[#a89984] border-[#665c54]/60',
  blue:  'bg-[#458588]/20 text-[#83a598] border-[#458588]/50',
};

const normaliseStatus = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (!s || s === 'not_provisioned' || s === 'none') return 'unordered';
  if (STATUS[s]) return s;
  if (s === 'provisioned' || s === 'complete' || s === 'success') return 'active';
  if (s === 'queued' || s === 'processing' || s === 'submitted') return 'pending';
  if (s === 'error' || s === 'rejected') return 'failed';
  return 'unordered';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

function StatusBadge({ status }) {
  const meta = STATUS[status] || STATUS.unordered;
  const Icon = meta.icon;
  return (
    <span title={meta.hint}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[11px] font-semibold whitespace-nowrap ${TONES[meta.tone]}`}>
      <Icon className="text-[10px]" /> {meta.label}
    </span>
  );
}

function Tile({ label, value, tone, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex-1 min-w-[120px] text-left p-3 rounded-xl border transition ${
        active ? 'border-[#fabd2f] bg-[#fabd2f]/10' : 'border-[#3c3836] bg-[#282828] hover:border-[#504945]'}`}>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-[11px] text-[#a89984] mt-0.5">{label}</div>
    </button>
  );
}

function Copyable({ v }) {
  if (!v) return <span className="text-[#665c54]">—</span>;
  return (
    <button type="button" title="Copy" onClick={() => navigator.clipboard?.writeText(v)}
      className="font-mono text-xs text-[#ebdbb2] hover:text-[#fabd2f] inline-flex items-center gap-1 group">
      {v}<FaCopy className="opacity-0 group-hover:opacity-60 text-[10px]" />
    </button>
  );
}

export default function ServiceOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const [rowMsg, setRowMsg] = useState({});
  // Live PPPoE session per row — is the customer actually online right now.
  const [online, setOnline] = useState({});

  // ── Load ───────────────────────────────────────────────────────────────────
  // Customer records are the source of truth for orders (provisionStatus +
  // vocusOrderSnapshot). Standalone provisions aren't tied to a customer, so they only
  // exist in the admin action log under the sentinel customerId 'standalone'.
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      const customers = Array.isArray(data?.customers) ? data.customers : Array.isArray(data) ? data : [];

      const rows = customers
        .filter((c) => c && (c.provisionStatus || c.vocusOrderSnapshot || c.status === 'active' || c.status === 'approved'))
        .map((c) => {
          const snap = c.vocusOrderSnapshot || {};
          return {
            key: c.UID || c.customerId || c.email,
            kind: 'customer',
            customerId: c.UID || c.customerId,
            email: c.email,
            name: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email,
            address: [c.unitNumber, c.streetNumber, c.streetName, c.streetType].filter(Boolean).join(' '),
            suburb: [c.suburb, c.state, c.postcode].filter(Boolean).join(' '),
            plan: c.plan || snap.planIdDescription || snap.vocusSpeedTier || null,
            status: normaliseStatus(c.provisionStatus),
            rawStatus: c.provisionStatus || null,
            loc: snap.directoryId || c.vocusSqSnapshot?.nbnLocationId || null,
            tech: snap.serviceType || c.vocusSqSnapshot?.accessTechnology || null,
            orderType: snap.orderType || (String(c.connectionType || '').toLowerCase() === 'transfer' ? 'TRANSFER' : null),
            avcid: snap.avcid || c.avcId || null,
            serviceId: snap.serviceId || c.vocusServiceId || null,
            transactionId: snap.transactionId || null,
            planId: snap.planId || null,
            username: snap.pppoeUsername || c.vocusPppoeUsername || null,
            // Assigned by Vocus once the service goes live (TCAS SUCCESS).
            nbnInstanceId: c.vocusNbnInstanceId || null,
            billingProviderId: c.vocusBillingProviderId || null,
            cvcId: c.vocusCvcId || null,
            dataPortId: c.vocusDataPortId || null,
            dataPort: snap.dataPortNumber || null,
            sentAt: snap.sentAt || null,
            lastStatus: snap.lastStatus || null,
            lastStatusAt: snap.lastStatusAt || null,
            snapshot: c.vocusOrderSnapshot || null,
          };
        });

      // Standalone provisions from the action log (best-effort — never blocks the table).
      let standalone = [];
      try {
        const hist = await adminService.getCustomerActionHistory('standalone', undefined, 100);
        const items = Array.isArray(hist?.history) ? hist.history : Array.isArray(hist?.actions) ? hist.actions : [];
        standalone = items
          .filter((h) => String(h?.metadata?.actionType || '').startsWith('provision'))
          .map((h, i) => {
            const d = h?.metadata?.actionDetails || {};
            return {
              key: h.id || `standalone-${i}`,
              kind: 'standalone',
              customerId: null,
              email: null,
              name: 'Standalone order',
              address: '', suburb: '',
              plan: d.planId || null,
              status: d.outcome === 'failed' ? 'failed' : 'pending',
              rawStatus: d.outcome || null,
              loc: d.directoryId || null,
              tech: null,
              orderType: d.orderType || null,
              avcid: null,
              serviceId: null,
              transactionId: d.transactionId || null,
              planId: d.planId || null,
              username: null, dataPort: null,
              sentAt: h.createdAt || null,
              lastStatus: d.outcome || null,
              lastStatusAt: h.createdAt || null,
              errorMessage: d.errorMessage || null,
              snapshot: d,
            };
          });
      } catch (histErr) {
        // Never let the audit-log lookup take the table down — customer-linked orders still render.
        console.warn('[service-orders] standalone history unavailable:', histErr?.message || histErr);
      }

      const all = [...rows, ...standalone].sort((a, b) => {
        const rank = (r) => (r.status === 'pending' ? 0 : r.status === 'failed' ? 1 : r.status === 'unordered' ? 2 : 3);
        const d = rank(a) - rank(b);
        if (d !== 0) return d;
        return String(b.sentAt || '').localeCompare(String(a.sentAt || ''));
      });
      setOrders(all);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Orders sit at 'pending' until someone asks Vocus what happened — the record only moves when
  // TCAS is polled. Waiting for a human to click every row means the board shows stale state
  // (a service can be live for days while the dashboard still says "in progress"), so poll the
  // in-flight ones automatically, once per load.
  const [autoPolling, setAutoPolling] = useState(false);
  const [autoPolled, setAutoPolled] = useState(false);

  // Only customer-linked orders carrying a transaction can be polled — a standalone order has
  // no record to update, so it never leaves 'in progress' here.
  const isPollable = (o) => o.kind === 'customer' && o.status === 'pending' && !!o.transactionId;

  const pollPending = useCallback(async (rows) => {
    const targets = rows.filter(isPollable);
    if (targets.length === 0) return false;
    setAutoPolling(true);
    let changed = false;
    // Sequential: each one is a Vocus round trip, and there are only ever a handful in flight.
    for (const row of targets) {
      try {
        const res = await adminService.checkVocusStatus({
          customerId: row.customerId, customerEmail: row.email, transactionId: row.transactionId,
        });
        const state = res?.vocusStatus || res?.transactionState || null;
        if (!res?.error && state && state !== row.lastStatus) changed = true;
        if (res?.error) {
          setRowMsg((m) => ({ ...m, [row.key]: { ok: false, text: res.details || res.error } }));
        }
      } catch (e) {
        setRowMsg((m) => ({ ...m, [row.key]: { ok: false, text: e?.message || String(e) } }));
      }
    }
    setAutoPolling(false);
    return changed;
  }, []);

  useEffect(() => {
    if (loading || autoPolled || orders.length === 0) return;
    setAutoPolled(true);
    (async () => {
      const changed = await pollPending(orders);
      if (changed) await load();
    })();
  }, [loading, autoPolled, orders, pollPending, load]);

  // ── Row actions ────────────────────────────────────────────────────────────
  const runRowAction = async (row, kind) => {
    if (!row.customerId && !row.email) return;
    const confirmMsg = kind === 'withdraw'
      ? `Withdraw the Vocus order for ${row.name}?\n\nThis cancels the in-flight order at Vocus (FIBRE::Delete PROVISIONING). Vocus enforces a 3-business-day window — earlier attempts fail with FIBRE-2205.`
      : kind === 'cancel'
        ? `Deactivate the LIVE Vocus service for ${row.name}?\n\nThis stops the AVC and ends billing on it. Only for services that are already ACTIVE.`
        : null;
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    setBusyRow(`${row.key}:${kind}`);
    setRowMsg((m) => ({ ...m, [row.key]: null }));
    try {
      const payload = { customerId: row.customerId, customerEmail: row.email, adminUser: 'admin-portal' };
      const res = kind === 'status'
        ? await adminService.checkVocusStatus({ ...payload, transactionId: row.transactionId || undefined })
        : kind === 'withdraw'
          ? await adminService.withdrawProvision({ ...payload, transactionId: row.transactionId || undefined })
          : await adminService.cancelVocusService({ ...payload, serviceId: row.serviceId || undefined });
      const ok = !res?.error;
      setRowMsg((m) => ({
        ...m,
        [row.key]: {
          ok,
          text: ok
            ? (res?.vocusStatus || res?.provisionStatus || res?.message || 'Done')
            : (res?.details || res?.error || 'Failed'),
        },
      }));
      if (ok) await load();
    } catch (e) {
      setRowMsg((m) => ({ ...m, [row.key]: { ok: false, text: e?.message || String(e) } }));
    } finally {
      setBusyRow(null);
    }
  };

  // ── Live service check ─────────────────────────────────────────────────────
  // FIBRE::Get Scope=SERVICE — what Vocus currently holds for the service: status, the AVC and
  // NBN instance it ended up on, the auth type actually in force, and the username Vocus
  // assigned. Read-only, and useful when the vendor portal is unavailable.
  //
  // NOTE this is service state, NOT "is the customer's router connected right now". Live PPPoE
  // session data (IP, last packet seen, double logins) lives behind OPER::Get ONLINE-SESSION,
  // which this account is not entitled to — it returns WSM-4221 Invalid ProductID, the same
  // blocker as ProductID NETWORK. The code for it already exists in check-vocus-availability
  // (mode GET_ONLINE_SESSION); it needs Vocus to enable ProductID OPER on the account.
  //
  // Called straight against the Vocus lambda with the admin token: operational modes require a
  // verified admin, so an unauthenticated caller gets nothing.
  const checkService = async (row) => {
    if (!row.serviceId) return;
    setBusyRow(`${row.key}:svc`);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({
          mode: 'GET_SERVICE_DETAILS',
          serviceId: row.serviceId,
          // The lambda normalises an address for every non-QUALIFY mode; this one doesn't use it.
          addressComponents: { postcode: '0000' },
        }),
      });
      const data = await res.json().catch(() => ({}));
      const p = data?.params || {};
      if (!res.ok || !p.ServiceStatus) {
        setOnline((m) => ({ ...m, [row.key]: { error: data?.message || data?.details || `HTTP ${res.status}` } }));
        return;
      }
      setOnline((m) => ({ ...m, [row.key]: { svc: p, checkedAt: new Date().toISOString() } }));
    } catch (e) {
      setOnline((m) => ({ ...m, [row.key]: { error: e?.message || String(e) } }));
    } finally {
      setBusyRow(null);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { all: orders.length, pending: 0, active: 0, failed: 0, unordered: 0, closed: 0 };
    for (const o of orders) {
      if (o.status === 'pending') c.pending++;
      else if (o.status === 'active') c.active++;
      else if (o.status === 'failed' || o.status === 'inactive') c.failed++;
      else if (o.status === 'unordered') c.unordered++;
      else c.closed++;
    }
    return c;
  }, [orders]);

  const pollableCount = orders.filter(isPollable).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter === 'pending' && o.status !== 'pending') return false;
      if (filter === 'active' && o.status !== 'active') return false;
      if (filter === 'failed' && !['failed', 'inactive'].includes(o.status)) return false;
      if (filter === 'unordered' && o.status !== 'unordered') return false;
      if (filter === 'closed' && !['withdrawn', 'cancelled'].includes(o.status)) return false;
      if (!q) return true;
      return [o.name, o.email, o.loc, o.serviceId, o.transactionId, o.avcid, o.address, o.suburb, o.username]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [orders, filter, query]);

  // Orders sitting in-flight for a while are the ones worth chasing.
  const stale = orders.filter((o) => o.status === 'pending' && (daysSince(o.sentAt) ?? 0) >= 5);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-3">
          <FaClipboardList className="text-2xl text-[#fabd2f]" />
          <h1 className="text-2xl font-bold text-[#ebdbb2]">Service Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button"
            onClick={async () => { const changed = await pollPending(orders); if (changed) await load(); }}
            disabled={loading || autoPolling || pollableCount === 0}
            title="Ask Vocus for the current state of every in-flight order"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-sm disabled:opacity-40">
            <FaSync className={autoPolling ? 'animate-spin' : ''} /> Poll Vocus ({pollableCount})
          </button>
          <button type="button" onClick={() => { setAutoPolled(false); load(); }} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-sm disabled:opacity-50">
            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-sm text-[#a89984] mb-5">
        Every NBN provisioning order and its live state. In-flight orders are polled against Vocus
        TCAS automatically when this page loads — an order stays "in progress" in our records until
        Vocus is asked, so nothing moves on its own without this.
        {autoPolling && <span className="ml-2 text-[#fabd2f]">Polling Vocus…</span>}
      </p>

      {error && (
        <div className="p-4 mb-4 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {stale.length > 0 && (
        <div className="p-3 mb-4 rounded-lg bg-amber-900/25 border border-amber-700/50 text-amber-200 text-sm flex items-start gap-2">
          <FaClock className="mt-0.5 shrink-0" />
          <span>
            {stale.length} order{stale.length > 1 ? 's have' : ' has'} been in progress for 5+ days
            ({stale.map((o) => o.name).join(', ')}). Check the status or chase Vocus.
          </span>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <Tile label="All orders"    value={counts.all}       tone="text-[#ebdbb2]" active={filter === 'all'}       onClick={() => setFilter('all')} />
        <Tile label="In progress"   value={counts.pending}   tone="text-[#fabd2f]" active={filter === 'pending'}   onClick={() => setFilter('pending')} />
        <Tile label="Active"        value={counts.active}    tone="text-[#b8bb26]" active={filter === 'active'}    onClick={() => setFilter('active')} />
        <Tile label="Failed"        value={counts.failed}    tone="text-[#fb4934]" active={filter === 'failed'}    onClick={() => setFilter('failed')} />
        <Tile label="Not ordered"   value={counts.unordered} tone="text-[#83a598]" active={filter === 'unordered'} onClick={() => setFilter('unordered')} />
        <Tile label="Withdrawn"     value={counts.closed}    tone="text-[#a89984]" active={filter === 'closed'}    onClick={() => setFilter('closed')} />
      </div>

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-3 text-[#665c54]" />
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, LOC, Service ID, transaction, AVC…"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] placeholder-[#665c54] focus:outline-none focus:border-[#fabd2f]" />
      </div>

      <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[#a89984] border-b border-[#3c3836]">
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2 font-semibold">Customer</th>
                <th className="px-3 py-2 font-semibold">Premise</th>
                <th className="px-3 py-2 font-semibold">Order</th>
                <th className="px-3 py-2 font-semibold">Service ID</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Ordered</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3c3836]">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#a89984]">Loading orders…</td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#a89984]">No orders match this filter.</td></tr>
              )}
              {!loading && visible.map((o) => {
                const open = expanded === o.key;
                const msg = rowMsg[o.key];
                const age = daysSince(o.sentAt);
                return [
                  <tr key={o.key} className="hover:bg-[#32302f] align-top">
                    <td className="px-3 py-3">
                      <button type="button" onClick={() => setExpanded(open ? null : o.key)}
                        className="text-[#a89984] hover:text-[#ebdbb2]" title="Details">
                        {open ? <FaChevronDown /> : <FaChevronRight />}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-[#ebdbb2]">{o.name}</div>
                      <div className="text-[11px] text-[#a89984]">{o.email || <span className="italic">not linked to a customer</span>}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-[#a89984] text-xs">{o.address || '—'}</div>
                      <div className="text-[11px] text-[#928374]">{o.suburb}</div>
                      <div className="text-[11px] font-mono text-[#83a598]">{o.loc || '—'}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-xs text-[#ebdbb2]">
                        {o.orderType || 'NEW'}{o.tech ? ` · ${o.tech}` : ''}
                      </div>
                      <div className="text-[11px] text-[#a89984]">{o.plan || '—'}</div>
                      {o.dataPort && <div className="text-[11px] text-[#928374]">UNI-D{o.dataPort}</div>}
                    </td>
                    <td className="px-3 py-3"><Copyable v={o.serviceId} /></td>
                    <td className="px-3 py-3">
                      <StatusBadge status={o.status} />
                      {o.lastStatus && <div className="text-[11px] text-[#928374] mt-1">Vocus: {o.lastStatus}</div>}
                      {msg && (
                        <div className={`text-[11px] mt-1 ${msg.ok ? 'text-[#b8bb26]' : 'text-[#fb4934]'}`}>{msg.text}</div>
                      )}
                      {online[o.key] && (
                        online[o.key].error ? (
                          <div className="text-[11px] mt-1 text-[#fb4934]">Service check: {online[o.key].error}</div>
                        ) : (
                          <div className="text-[11px] mt-1">
                            <span className={online[o.key].svc.ServiceStatus === 'ACTIVE' ? 'text-[#b8bb26]' : 'text-[#fabd2f]'}>
                              Vocus: {online[o.key].svc.ServiceStatus}
                            </span>
                            <div className="text-[#928374] font-mono">
                              {[online[o.key].svc.AuthType, online[o.key].svc.IPAssignment,
                                online[o.key].svc.AVCID].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        )
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-[#a89984]">
                      {fmtDate(o.sentAt)}
                      {age != null && o.status === 'pending' && (
                        <div className={`text-[11px] ${age >= 5 ? 'text-[#fabd2f]' : 'text-[#928374]'}`}>{age}d ago</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {o.kind === 'customer' ? (
                        <div className="inline-flex flex-col items-end gap-1">
                          <button type="button" onClick={() => runRowAction(o, 'status')}
                            disabled={busyRow === `${o.key}:status` || !o.transactionId}
                            title={o.transactionId ? 'Poll Vocus TCAS for this transaction' : 'No transaction to poll'}
                            className="px-2.5 py-1 rounded border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-[11px] disabled:opacity-40">
                            {busyRow === `${o.key}:status` ? 'Checking…' : 'Check status'}
                          </button>
                          {o.status === 'pending' && (
                            <button type="button" onClick={() => runRowAction(o, 'withdraw')}
                              disabled={busyRow === `${o.key}:withdraw`}
                              className="px-2.5 py-1 rounded border border-[#cc241d]/50 bg-[#cc241d]/15 hover:bg-[#cc241d]/25 text-[#fb4934] text-[11px] disabled:opacity-40">
                              {busyRow === `${o.key}:withdraw` ? 'Withdrawing…' : 'Withdraw'}
                            </button>
                          )}
                          {o.serviceId && (
                            <button type="button" onClick={() => checkService(o)}
                              disabled={busyRow === `${o.key}:svc`}
                              title="Ask Vocus what it currently holds for this service"
                              className="px-2.5 py-1 rounded border border-[#458588]/50 bg-[#458588]/15 hover:bg-[#458588]/25 text-[#83a598] text-[11px] disabled:opacity-40">
                              {busyRow === `${o.key}:svc` ? 'Checking…' : 'Service check'}
                            </button>
                          )}
                          {o.status === 'active' && (
                            <button type="button" onClick={() => runRowAction(o, 'cancel')}
                              disabled={busyRow === `${o.key}:cancel`}
                              className="px-2.5 py-1 rounded border border-[#cc241d]/50 bg-[#cc241d]/15 hover:bg-[#cc241d]/25 text-[#fb4934] text-[11px] disabled:opacity-40">
                              {busyRow === `${o.key}:cancel` ? 'Cancelling…' : 'Deactivate'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#665c54]">no record to act on</span>
                      )}
                    </td>
                  </tr>,
                  open && (
                    <tr key={`${o.key}-detail`} className="bg-[#1d2021]">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          {[
                            ['Transaction ID', o.transactionId],
                            ['LOC / DirectoryID', o.loc],
                            ['PlanID', o.planId],
                            ['AVC ID', o.avcid],
                            ['PPPoE username', o.username],
                            ['UNI-D port', o.dataPort],
                            ['Last Vocus state', o.lastStatus],
                            ['Last checked', o.lastStatusAt ? fmtDate(o.lastStatusAt) : null],
                            ['NBN instance ID', o.nbnInstanceId],
                            ['Billing provider ID', o.billingProviderId],
                            ['CVC ID', o.cvcId],
                            ['Data port', o.dataPortId],
                          ].map(([k, v]) => (
                            <div key={k}>
                              <div className="text-[11px] text-[#a89984] mb-0.5">{k}</div>
                              <Copyable v={v ? String(v) : ''} />
                            </div>
                          ))}
                        </div>
                        {o.errorMessage && (
                          <div className="mt-3 p-2 rounded bg-red-900/30 border border-red-800/50 text-[11px] text-red-200">
                            {o.errorMessage}
                          </div>
                        )}
                        {o.status === 'unordered' && (
                          <p className="mt-3 text-[11px] text-[#83a598]">
                            No Vocus order on this customer yet — provision them from the Provision Service tab.
                          </p>
                        )}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-[#665c54] mt-3">
        Withdraw cancels an in-flight order (Vocus enforces a 3-business-day window; earlier attempts
        return FIBRE-2205). Deactivate stops a live AVC and ends billing on it.
      </p>
    </div>
  );
}
